import { getAjv } from './getAjv';
/**
 * @todo eventBoundary and allowEventPropagation
 * - Each app loaded using embedMiniApp should have an eventBoundary value which corresponds with it's parentInstanceId
 * - The EventBus should check if the dispatching app has an eventBoundary and if so ensure that only subscribers within that
 * boundary are notified
 *
 * @todo dispatch scope/direction
 * - dispatch to specific appId or in a specific direction (eg. downward, upward)
 */
export class AbstractEventBus {
    constructor() {
        // Registered listeners indexed by eventName, instanceId
        this.listeners = {};
    }
    /**
     * Publishes an event, notifies subscribes and returns an array containing the appId and listener return values
     * from all subscribers
     */
    async publish(instanceId, eventNameOrConfig, data) {
        const eventConfig = typeof eventNameOrConfig === 'object' ? eventNameOrConfig : { eventName: eventNameOrConfig, appId: undefined };
        const { eventName } = eventConfig;
        const sourceApp = this.getAppMetaDataForInstanceId(instanceId);
        if (!sourceApp) {
            throw new Error(`Unable to publish event ${eventName}. App metadata could not be resolved for instance ${instanceId}`);
        }
        // If dispatchRules are defined for an app, they're enforced. If not defined, the app can dispatch anything
        if (sourceApp.dispatchRules && !sourceApp.dispatchRules[eventName]) {
            throw new Error(`App '${sourceApp.id}' is not allowed to dispatch event '${eventName}'`);
        }
        // @todo review if we can terminate here for nabx: events
        // Backward compatibility for XV1 apps which use window for event management
        if (eventConfig.eventName.match(/^nabx:/)) {
            window.dispatchEvent(new CustomEvent(eventConfig.eventName, {
                bubbles: true,
                composed: true,
                detail: data
            }));
        }
        const responses = [];
        // Do nothing if there are no listeners
        if (!this.listeners[eventName] || !Object.keys(this.listeners[eventName]).length) {
            return responses;
        }
        // Validate data against schema if schema has been provided & reject if invalid
        if (sourceApp.dispatchRules && sourceApp.dispatchRules[eventName].schema) {
            const ajv = getAjv();
            if (!ajv) {
                // eslint-disable-next-line no-console
                console.warn('Warning: ajv schema validator not found. Schema validation will not be performed for events');
            }
            else {
                const validate = await ajv.compileAsync(sourceApp.dispatchRules[eventName].schema);
                if (!validate(data || null)) {
                    const errors = (validate.errors || []).map((e) => JSON.stringify(e, null, 2)).join('\n');
                    throw new Error(`Event data supplied by app '${sourceApp.id}' for event '${eventName}' does not match the defined schema. Errors: \n${errors}`);
                }
            }
        }
        // Invoke listeners
        for (const listenerInstanceId of Object.keys(this.listeners[eventName])) {
            const targetApp = this.getAppMetaDataForInstanceId(listenerInstanceId);
            const rules = targetApp.listenerRules && targetApp.listenerRules[eventName];
            if (targetApp && (!targetApp.listenerRules || !rules.sourceAppIds || rules.sourceAppIds.indexOf(sourceApp.id) !== -1)) {
                // Where the event targets a specific appId, ensure only listeners from that app are invoked
                if (eventConfig.appId) {
                    const appIds = Array.isArray(eventConfig.appId) ? eventConfig.appId : [eventConfig.appId];
                    if (appIds.indexOf(targetApp.id) === -1) {
                        continue;
                    }
                }
                // Where the event targets a specific instanceId, ensure only listeners from that instance are invoked
                if (eventConfig.instanceId) {
                    const instanceIds = Array.isArray(eventConfig.instanceId) ? eventConfig.instanceId : [eventConfig.instanceId];
                    if (instanceIds.indexOf(listenerInstanceId) === -1) {
                        continue;
                    }
                }
                this.listeners[eventName][listenerInstanceId].forEach((l) => {
                    if (l.config && l.config.appId) {
                        const appIds = Array.isArray(l.config.appId) ? l.config.appId : [l.config.appId];
                        if (appIds.indexOf(sourceApp.id) === -1) {
                            return;
                        }
                    }
                    if (l.config && l.config.instanceId) {
                        const instanceIds = Array.isArray(l.config.instanceId) ? l.config.instanceId : [l.config.instanceId];
                        if (instanceIds.indexOf(instanceId) === -1) {
                            return;
                        }
                    }
                    responses.push({
                        appId: targetApp.id,
                        instanceId: listenerInstanceId,
                        response: l.listener(data)
                    });
                });
            }
        }
        return responses;
    }
    /**
     * Subscribe an app instance to an event
     */
    subscribe(instanceId, eventNameOrConfig, listener) {
        const eventConfig = typeof eventNameOrConfig === 'object' ? eventNameOrConfig : { eventName: eventNameOrConfig, appId: undefined };
        const { eventName } = eventConfig;
        const app = this.getAppMetaDataForInstanceId(instanceId);
        if (!app) {
            throw new Error(`Unable to subscribe to event ${eventName}. App metadata could not be resolved for instance ${instanceId}`);
        }
        // If listener rules are defined look for listener rule and ensure app is allowed to listen for the event
        if (app.listenerRules && !app.listenerRules[eventName]) {
            throw new Error(`App '${app.id}' is not allowed to listen for event '${eventName}'`);
        }
        this.listeners[eventName] = this.listeners[eventName] || {};
        this.listeners[eventName][instanceId] = this.listeners[eventName][instanceId] || [];
        const reg = {
            listener,
            config: eventConfig
        };
        if (!this.listeners[eventName][instanceId].find((r) => {
            return r.listener === listener;
            // @todo compare list of appId's
        })) {
            // Backward compatibility for XV1 apps which use window for event management
            if (eventConfig.eventName.match(/^nabx:/)) {
                reg.windowListener = (event) => {
                    listener(event.detail);
                };
                window.addEventListener(eventConfig.eventName, reg.windowListener);
            }
            this.listeners[eventName][instanceId].push(reg);
        }
        return () => this.unsubscribe(instanceId, eventName, listener);
    }
    /**
     * Unsubscribe a listener registered by a specific app instance for a specific event name
     *
     * If a specific listener fn is supplied here, only that listener fn will be removed with all other listeners
     * for the given instanceId and eventName being retained. If no specific listener fn is supplied, ALL of the event
     * listeners for the given instanceId and event name will be removed.
     */
    unsubscribe(instanceId, eventName, listener) {
        const app = this.getAppMetaDataForInstanceId(instanceId);
        if (!app) {
            throw new Error(`Unable to unsubscribe from event ${eventName}. App metadata could not be resolved for instance ${instanceId}`);
        }
        if (this.listeners[eventName] && this.listeners[eventName][instanceId]) {
            this.listeners[eventName][instanceId] = this.listeners[eventName][instanceId].filter((l) => {
                // Remove window listener if one is registered
                if (l.windowListener && (!listener || listener === l.listener)) {
                    window.removeEventListener(eventName, l.windowListener);
                }
                return listener ? l.listener !== listener : false;
            });
            if (!this.listeners[eventName][instanceId].length) {
                delete this.listeners[eventName][instanceId];
            }
            if (!Object.keys(this.listeners[eventName]).length) {
                delete this.listeners[eventName];
            }
        }
    }
    /**
     * Unsubscribe all listeners for a specific app instance
     */
    unsubscribeInstance(instanceId) {
        for (const eventName of Object.keys(this.listeners)) {
            this.unsubscribe(instanceId, eventName);
        }
    }
}
//# sourceMappingURL=AbstractEventBus.js.map